'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { memo } from 'react';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIHeader from 'UIComponents/layout/UIHeader';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UICardHeader from 'UIComponents/card/UICardHeader';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIToolBar from 'UIComponents/nav/UIToolBar';
import UIToolBarGroup from 'UIComponents/nav/UIToolBarGroup';
import { CampaignDetailsTabTypes, EntityTypes } from 'AdsUI/lib/Constants';
import makeLoadable from 'AdsUI/lib/makeLoadable';
import CampaignDetailsHeader from 'AdsUI/components/campaign-details/CampaignDetailsHeader';
import CampaignDetailsTabsContainer from 'AdsUI/containers/campaign-details/CampaignDetailsTabsContainer';
import GlobalTimeRangeFilterContainer from 'AdsUI/components/GlobalTimeRangeFilterContainer';
import TimeZoneMessage from 'AdsUI/components/TimeZoneMessage';
import { XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
const VisualizationLoader = makeLoadable(() => import('reporting-visualizations/Visualization'));

const CampaignDetailsAnalyzeHeaderImpl = ({
  campaignDetails,
  network,
  adAccount
}) => !campaignDetails || !campaignDetails.entity ? /*#__PURE__*/_jsx(UIHeader, {
  children: /*#__PURE__*/_jsx(UILoadingSpinner, {
    grow: true
  })
}) : /*#__PURE__*/_jsx(CampaignDetailsHeader, {
  entity: campaignDetails.entity,
  entityType: EntityTypes.CAMPAIGN,
  network: network,
  adAccount: adAccount
});

const CampaignDetailsAnalyzeHeader = /*#__PURE__*/memo(CampaignDetailsAnalyzeHeaderImpl);

const buildCampaignDetailsBreakdownDataMap = breakdowns => {
  const data = Object.keys(breakdowns).reduce((list, platform) => list.concat(breakdowns[platform].map(funnel => {
    return {
      platform,
      device: I18n.text(`adDetails.placementChart.impressionDevice.${funnel.impressionDevice}`),
      reach: funnel.reach
    };
  })), []);
  const properties = {
    platform: {
      type: 'enumeration',
      label: I18n.text('leadAdCampaignDetails.placementChart.platform'),
      references: Object.keys(breakdowns).reduce((object, platform) => Object.assign({}, object, {
        [platform]: {
          label: I18n.text(`adDetails.placementChart.platforms.${platform}`)
        }
      }), {})
    },
    reach: {
      type: 'number',
      label: I18n.text('leadAdCampaignDetails.placementChart.reach')
    }
  };
  return {
    aggregated: {
      data,
      properties
    }
  };
};

const CampaignDetailsBreakdownChartImpl = ({
  campaignBreakdowns
}) => {
  if (campaignBreakdowns.isEmpty()) {
    return /*#__PURE__*/_jsx("div", {
      className: "text-center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "leadAdCampaignDetails.placementChart.noData"
      })
    });
  } else {
    const viz = XYVisualization({
      type: 'XY',
      data: buildCampaignDetailsBreakdownDataMap(campaignBreakdowns.toJS()),
      layers: [{
        dataset: 'aggregated',
        mark: {
          type: 'bar',
          stacking: true
        },
        y: {
          type: 'category',
          field: 'platform'
        },
        x: {
          type: 'linear',
          field: 'reach'
        },
        color: {
          field: 'device'
        }
      }],
      style: {
        orient: 'horizontal',
        showTotals: false,
        stackLabels: true
      }
    });
    return /*#__PURE__*/_jsx(VisualizationLoader, {
      visualization: viz
    });
  }
};

const CampaignDetailsBreakdownChart = /*#__PURE__*/memo(CampaignDetailsBreakdownChartImpl);

const CampaignDetailsAnalyze = ({
  campaignDetails,
  campaignBreakdowns,
  loadingBreakdowns,
  network,
  adAccount
}) => /*#__PURE__*/_jsxs(UIDashboardPage, {
  headerComponent: /*#__PURE__*/_jsx(CampaignDetailsAnalyzeHeader, {
    campaignDetails: campaignDetails,
    network: network,
    adAccount: adAccount
  }),
  className: "campaign-details-page",
  tabs: /*#__PURE__*/_jsx(NavMarker, {
    name: "CAMPAIGN_DETAILS_TABS",
    children: /*#__PURE__*/_jsx(CampaignDetailsTabsContainer, {
      network: network,
      activeTab: CampaignDetailsTabTypes.ANALYZE
    })
  }),
  pageLayout: "full-width",
  children: [/*#__PURE__*/_jsxs(UIToolBar, {
    use: "filter-bar",
    className: "m-top-0 m-bottom-4",
    children: [/*#__PURE__*/_jsx(UIToolBarGroup, {
      children: /*#__PURE__*/_jsx(GlobalTimeRangeFilterContainer, {
        placement: "bottom right"
      })
    }), /*#__PURE__*/_jsx(UIToolBarGroup, {})]
  }), /*#__PURE__*/_jsx(UICardGrid, {
    children: /*#__PURE__*/_jsxs(UICardWrapper, {
      children: [/*#__PURE__*/_jsx(UICardHeader, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "leadAdCampaignDetails.placementChart.title"
        })
      }), loadingBreakdowns ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        className: "p-y-10",
        grow: true
      }) : /*#__PURE__*/_jsx(UICardSection, {
        "data-test-id": "campaign-details-analyze",
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "CAMPAGIN_DETAILS_BREAKDOWN_CHART",
          children: /*#__PURE__*/_jsx(CampaignDetailsBreakdownChart, {
            campaignBreakdowns: campaignBreakdowns
          })
        })
      })]
    })
  }), /*#__PURE__*/_jsx(TimeZoneMessage, {})]
});

export default /*#__PURE__*/memo(CampaignDetailsAnalyze);